import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { ArrowLeft, SprayCan, ChevronRight, FileDown, ShieldAlert, Send, FileCheck2, Printer } from "lucide-react";

export const STATUS_STYLE = {
  recommended: "bg-amber-500 text-white",
  draft: "bg-stone-200 text-stone-700",
  reviewed: "bg-sky-600 text-white",
  approved: "bg-[#7C9673] text-white",
  completed: "bg-[#1F3D34] text-white",
  cancelled: "bg-red-100 text-red-700",
};
const FILTERS = ["all", "draft", "reviewed", "approved", "completed", "cancelled"];

export default function SprayOrders() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const isManager = ["admin", "supervisor"].includes(user?.role);
  const isDriver = user?.role === "tractor_driver";
  const [orders, setOrders] = useState(null);
  const [rei, setRei] = useState([]);
  const [purs, setPurs] = useState([]);
  const [showPurs, setShowPurs] = useState(false);
  const [filter, setFilter] = useState("all");
  const [purMonth, setPurMonth] = useState(new Date().toISOString().slice(0, 7));
  const [purBusy, setPurBusy] = useState(false);
  const [purMsg, setPurMsg] = useState("");

  const exportPur = async () => {
    setPurBusy(true); setPurMsg("");
    try {
      const res = await api.get(`/pest/pur-export?month=${purMonth}`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `PUR-${purMonth}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (e) {
      setPurMsg(e.response?.status === 404 ? `No verified applications found for ${purMonth}` : "Export failed");
    } finally { setPurBusy(false); }
  };

  useEffect(() => {
    api.get("/pest/spray-orders").then(({ data }) => setOrders(data));
    api.get("/pest/rei-board").then(({ data }) => setRei(data)).catch(() => {});
    if (["admin", "supervisor"].includes(user?.role)) {
      api.get("/pest/pur-records").then(({ data }) => setPurs(data)).catch(() => {});
    }
  }, []);
  const sendPur = async (pid) => {
    const { data } = await api.post(`/pest/pur-records/${pid}/send`);
    setPurs((s) => s.map((p) => (p.id === pid ? data : p)));
  };
  const exportPurCsv = async () => {
    const res = await api.get("/pest/pur-records/export", { responseType: "blob" });
    const url = URL.createObjectURL(res.data);
    const a = document.createElement("a"); a.href = url; a.download = "PUR-records.csv";
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  if (!orders) return <div className="p-8 font-bold uppercase text-sm">Loading spray work orders…</div>;

  const rows = orders.filter((o) => filter === "all" || o.status === filter);
  return (
    <div className="space-y-5" data-testid="spray-orders-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          {!isDriver && (
            <button onClick={() => navigate("/pest")} className="text-xs font-bold uppercase text-stone-500 flex items-center gap-1 mb-2"><ArrowLeft size={13} /> Pest &amp; Disease</button>
          )}
          <h1 className="text-4xl sm:text-5xl font-semibold tracking-tight" style={{ fontFamily: "'Fraunces', serif" }}>Spray Work Orders</h1>
          <p className="text-stone-500 text-sm mt-1">{isDriver ? "Your assigned field sheets — read before mixing." : "Field instruction sheets and application records."}</p>
        </div>
        {isManager && (
          <div className="text-xs font-semibold text-stone-500 bg-white border border-stone-200 px-4 h-12 flex items-center" data-testid="create-from-planner-hint">
            Work orders arrive here once approved in the <button onClick={() => navigate("/pest/planner")} className="underline font-bold text-[#1F3D34] mx-1">Weekly Spray Plan</button> or created manually
          </div>
        )}
      </div>

      {!isDriver && (
        <div className="flex flex-wrap gap-2">
          {FILTERS.map((f) => (
            <button key={f} data-testid={`order-filter-${f}`} onClick={() => setFilter(f)}
              className={`h-10 px-4 text-xs font-bold uppercase border ${filter === f ? "bg-[#1F3D34] text-white border-[#1F3D34]" : "bg-white text-stone-600 border-stone-300"}`}>
              {f}
            </button>
          ))}
        </div>
      )}

      {rei.length > 0 && (
        <div className="border border-red-300 bg-red-50 p-4 space-y-2" data-testid="rei-active-board">
          <div className="font-bold uppercase text-xs text-red-700 flex items-center gap-2"><ShieldAlert size={15} /> Active Re-Entry Restrictions — Do Not Enter</div>
          {rei.map((r) => (
            <div key={r.order_id} className="flex flex-wrap items-center justify-between gap-2 bg-white border border-red-200 p-3" data-testid={`rei-row-${r.wo_number}`}>
              <div className="text-sm">
                <span className="font-bold">Ranch {r.ranch_number} — {r.vineyard_name}</span>
                <span className="text-stone-600"> · {(r.blocks || []).join(", ")} · {(r.products || []).join(" + ")}</span>
                <div className="text-xs text-stone-500 font-mono">safe re-entry {new Date(r.safe_reentry).toLocaleString()}</div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-black text-red-700 whitespace-nowrap" data-testid={`rei-countdown-${r.wo_number}`}>{r.hours_remaining}h remaining</span>
                <button data-testid={`rei-sheet-${r.wo_number}`} onClick={async () => {
                  const res = await api.get(`/pest/spray-orders/${r.order_id}/rei-sheet`, { responseType: "blob" });
                  const url = URL.createObjectURL(res.data);
                  const a = document.createElement("a"); a.href = url; a.download = `REI-${r.wo_number}.pdf`;
                  document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
                }} className="h-9 px-3 bg-red-700 text-white font-bold uppercase text-[10px] flex items-center gap-1"><Printer size={12} /> Posting Sheet</button>
              </div>
            </div>
          ))}
        </div>
      )}

      {isManager && (
        <div className="bg-white border border-stone-200 p-3 flex flex-wrap items-center gap-3" data-testid="pur-export-panel">
          <div className="text-xs font-bold uppercase text-stone-600 flex items-center gap-1.5"><FileDown size={14} /> California PUR Export</div>
          <input type="month" data-testid="pur-month-input" value={purMonth} onChange={(e) => setPurMonth(e.target.value)} className="h-10 px-3 border border-stone-300 text-sm" />
          <button data-testid="pur-export-btn" onClick={exportPur} disabled={purBusy || !purMonth}
            className="h-10 px-4 bg-[#7C9673] text-white font-bold uppercase text-xs disabled:opacity-40">
            {purBusy ? "Building…" : "Download XLSX"}
          </button>
          <span className="text-[11px] text-stone-500">Verified applications only — pesticide use report for the county.</span>
          {purMsg && <span className="text-xs font-semibold text-red-700" data-testid="pur-msg">{purMsg}</span>}
          <button data-testid="toggle-pur-records" onClick={() => setShowPurs(!showPurs)}
            className="h-10 px-4 border border-[#1F3D34] text-[#1F3D34] font-bold uppercase text-xs flex items-center gap-1 ml-auto">
            <FileCheck2 size={13} /> PUR Records ({purs.length})
          </button>
        </div>
      )}

      {isManager && showPurs && (
        <div className="bg-white border border-stone-200" data-testid="pur-records-panel">
          <div className="flex items-center justify-between p-3 border-b border-stone-200">
            <div className="text-xs font-bold uppercase text-stone-600">PUR Submission Records — one per applied work order</div>
            <button data-testid="pur-csv-btn" onClick={exportPurCsv} className="h-9 px-3 border border-stone-300 font-bold uppercase text-[10px] flex items-center gap-1"><FileDown size={12} /> CSV</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm min-w-[760px]">
              <thead><tr className="text-left text-[10px] uppercase text-stone-500 border-b border-stone-200">
                <th className="p-3">WO</th><th className="p-3">Site</th><th className="p-3">Applied</th><th className="p-3">Acres</th>
                <th className="p-3">Products</th><th className="p-3">Applicator</th><th className="p-3">Status</th><th className="p-3"></th>
              </tr></thead>
              <tbody>
                {purs.length === 0 && <tr><td colSpan={8} className="p-6 text-center text-stone-400 font-bold uppercase text-xs">No PUR records — use "Create PUR" on an applied work order</td></tr>}
                {purs.map((p) => (
                  <tr key={p.id} className="border-b border-stone-100" data-testid={`pur-row-${p.wo_number}`}>
                    <td className="p-3 font-mono text-xs">{p.wo_number}</td>
                    <td className="p-3 text-xs">{p.ranch_number} · {p.site_name}</td>
                    <td className="p-3 font-mono text-xs">{p.applied_date}</td>
                    <td className="p-3 text-xs">{p.treated_acres}</td>
                    <td className="p-3 text-xs">{(p.products || []).map((x) => x.product_name).join(", ")}</td>
                    <td className="p-3 text-xs">{p.applicator_name || "—"}</td>
                    <td className="p-3">
                      <span className={`text-[10px] font-bold uppercase px-2 py-1 ${p.sent_status === "sent" ? "bg-[#E8EFE6] text-[#1F3D34]" : "bg-amber-100 text-amber-800"}`} data-testid={`pur-status-${p.wo_number}`}>
                        {p.sent_status === "sent" ? `Sent ${p.date_filed}` : "Not Sent"}
                      </span>
                    </td>
                    <td className="p-3">
                      {p.sent_status !== "sent" && (
                        <button data-testid={`pur-send-${p.wo_number}`} onClick={() => sendPur(p.id)}
                          className="h-9 px-3 bg-[#1F3D34] text-white font-bold uppercase text-[10px] flex items-center gap-1"><Send size={11} /> Submit</button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <div className="space-y-2">
        {rows.map((o) => (
          <button key={o.id} data-testid={`order-card-${o.wo_number}`} onClick={() => navigate(`/pest/orders/${o.id}`)}
            className="w-full text-left bg-white border border-stone-200 p-4 flex flex-wrap items-center gap-3 hover:border-[#1F3D34]">
            <SprayCan size={20} className="text-[#7C9673] shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="font-bold flex flex-wrap items-center gap-2">
                <span className="font-mono">{o.wo_number}</span>
                <span className={`text-[10px] font-bold uppercase px-2 py-0.5 ${STATUS_STYLE[o.status] || "bg-stone-200"}`}>{o.status}</span>
                <span className="text-stone-500 font-normal text-sm">{o.target}</span>
              </div>
              <div className="text-xs text-stone-600 mt-0.5">
                {o.client_name ? `${o.client_name} · ` : ""}{o.vineyard_name} · {(o.blocks || []).map((b) => b.block).join(", ")} · {o.treated_acres} ac treated
              </div>
              <div className="text-xs text-stone-500">
                {o.date ? `Scheduled ${o.date} · ` : ""}{o.calc?.tanks_label} · {o.gpa} GPA{o.applicator ? ` · ${o.applicator}` : ""}
              </div>
            </div>
            <ChevronRight size={18} className="text-stone-400 shrink-0" />
          </button>
        ))}
        {rows.length === 0 && (
          <div className="bg-white border border-stone-200 p-10 text-center text-stone-400 font-bold uppercase text-sm" data-testid="orders-empty">
            {isDriver ? "No spray work orders assigned yet" : "No spray work orders"}
          </div>
        )}
      </div>
    </div>
  );
}
